import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react';

import CookieStorageService from 'services/CookieStorageService';
import UserStore from 'stores/UserStore';
import ACCESS_LEVEL_NUM from 'enum/accessLevelsNum';

import {
  getConsentData,
  getUpdatedConsentData,
  postAcceptCookies,
  postAcceptUpdatedCookies,
} from './api';
import CookiePolicy from './CookiePolicy';
import CookiePolicyUpdated from './CookiePolicyUpdated';
import styles from './index.css';


const CookieConsent = () => {
  const { user: { accessLevel, username } } = UserStore;

  const [isPolicyVisible, setIsPolicyVisible] = useState(false);
  const [isPolicyUpdatedVisible, setIsPolicyUpdatedVisible] = useState(false);

  const isGuestBilling = accessLevel === ACCESS_LEVEL_NUM.GUEST_BILLING;
  const isLoggedInUser = username && !isGuestBilling;

  const getData = useCallback(async() => {

    if (!isLoggedInUser) {
      const shouldShowPolicy = !CookieStorageService.get('CAM4AcceptCookieConsent');
      setIsPolicyVisible(shouldShowPolicy);

      return;
    }

    const { data, error } = await getConsentData(username);
    const consentData = data?.find((item) => item.consent?.consentType === 'COOKIES');
    if (!data || !consentData || error) {
      setIsPolicyVisible(true);

      return;
    }

    const {
      consent: { updatedAt: policyUpdatedAt },
      userConsentCreatedAt,
      userConsentStatus,
    } = consentData;

    const isPolicyAccepted = userConsentStatus === 'APPROVED';
    setIsPolicyVisible(!isPolicyAccepted);

    if (!isPolicyAccepted) {
      return;
    }

    const {
      data: updatedConsentData,
      error: updatedConsentError,
    } = await getUpdatedConsentData(username);

    if (!updatedConsentData || updatedConsentError) {
      return;
    }

    const userConsentCreatedDate = new Date(userConsentCreatedAt)?.getTime();
    const policyUpdatedDate = new Date(policyUpdatedAt)?.getTime();
    const hasConsentVersionChanged = policyUpdatedDate > userConsentCreatedDate;
    if (!hasConsentVersionChanged) {
      return;
    }

    const updatedConsentDate = new Date(updatedConsentData?.[0]?.updatedAt)?.getTime();
    const shouldShow = (!updatedConsentDate || userConsentCreatedDate > updatedConsentDate);

    setIsPolicyUpdatedVisible(shouldShow);
  }, [isLoggedInUser, username]);

  useEffect(() => {
    getData();
    // eslint-disable-next-line -- componentDidUpdate
  }, [isLoggedInUser, username]);

  const onAcceptCookiesUpdate = useCallback(() => {
    setIsPolicyUpdatedVisible(false);

    if (isLoggedInUser) {
      postAcceptUpdatedCookies(username);
    }
  }, [isLoggedInUser, username]);

  const onAcceptCookies = useCallback(() => {
    setIsPolicyVisible(false);

    if (isLoggedInUser) {
      postAcceptCookies(username);
    } else {
      CookieStorageService.set('CAM4AcceptCookieConsent', true, { path: '/' });
    }
  }, [isLoggedInUser, username]);

  const cookiePolicyUpdatedElement = useMemo(() => {
    if (!username || !isPolicyUpdatedVisible) {
      return null;
    }

    return (
      <CookiePolicyUpdated onAccept={onAcceptCookiesUpdate} />
    );
  }, [
    isPolicyUpdatedVisible,
    username,
    onAcceptCookiesUpdate,
  ]);

  const cookiePolicyElement = useMemo(() => {
    if (!isPolicyVisible) {
      return null;
    }

    return (
      <CookiePolicy
        username={username}
        onAccept={onAcceptCookies}
      />
    );
  }, [
    isPolicyVisible,
    username,
    onAcceptCookies,
  ]);

  const isAnyPolicyVisible = isPolicyVisible || isPolicyUpdatedVisible;
  if (!isAnyPolicyVisible) {
    return null;
  }

  return (
    <div className={styles.cookieConsent}>
      {cookiePolicyUpdatedElement}
      {cookiePolicyElement}
    </div>
  );
};

export default memo(observer(CookieConsent));
